import React from 'react';
import {
    Modal,
    Form,
    Input,
    Tooltip,
    Checkbox,
    Button,
    message
} from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import {FormInstance} from "antd/lib/form";
import {api} from "../../../service/apiClient";
import { finalize } from 'rxjs/operators';
import { useState } from 'react';
import styled from "styled-components";

const formItemLayout = {
    labelCol: {
        xs: { span: 24 },
        sm: { span: 8 },
    },
    wrapperCol: {
        xs: { span: 24 },
        sm: { span: 16 },
    },
};
const tailFormItemLayout = {
    wrapperCol: {
        xs: {
            span: 24,
            offset: 0,
        },
        sm: {
            span: 16,
            offset: 8,
        },
    },
};


type NewUserFormProps = {
    modal: CreateUserModal
}

const formRef = React.createRef<FormInstance>(); // Link form submit event to modal button
const NewUserForm = ({modal}: NewUserFormProps) => {

    const [form] = Form.useForm();
    const [checked, setChecked] = useState(false)

    // On valid form submission
    const onFinish = (values: any) => {
        modal.setState({confirmLoading: true});
        // TODO - test with no server running
        api.createUser(values.email, values.password, values.name)
            .pipe(finalize(() => modal.setState({confirmLoading: false})))
            .subscribe((created: boolean) => {
                if (created) {
                    modal.setState({visible: false});
                    formRef.current!.resetFields();
                    message.success("User created")
                } else {
                    message.error("User/password denied")
                }
            }, error => {
                message.error(error)
            })
    };

    const toggleCheck = () => {
        setChecked((was) => { return !was })
    }

    return (
        <Form
            {...formItemLayout}
            form={form}
            name="register"
            onFinish={onFinish}
            scrollToFirstError
            ref={formRef}
        >
            <Form.Item
                name="email"
                label="E-mail"
                rules={[
                    {
                        type: 'email',
                        message: 'Invalid E-mail',
                    },
                    {
                        required: true,
                        message: 'Please input your E-mail',
                    },
                ]}
            >
                <Input/>
            </Form.Item>

            <Form.Item
                name="password"
                label="Password"
                rules={[
                    {
                        required: true,
                        message: 'Please input your password',
                    },
                ]}
                hasFeedback
            >
                <Input.Password/>
            </Form.Item>

            <Form.Item
                name="confirm"
                label="Confirm Password"
                dependencies={['password']}
                hasFeedback
                rules={[
                    {
                        required: true,
                        message: 'Please confirm your password',
                    },
                    ({getFieldValue}) => ({
                        validator(rule, value) {
                            if (!value || getFieldValue('password') === value) {
                                return Promise.resolve();
                            }
                            return Promise.reject('The two passwords that you entered do not match');
                        },
                    }),
                ]}
            >
                <Input.Password/>
            </Form.Item>

            <Form.Item
                name="name"
                rules={[{required: true, message: 'Please input your name', whitespace: true}]}
                label={
                    <span>Name&nbsp;
                        <Tooltip title="What do your friends call you? (i.e 'John Doe' or 'Worlds best Engineer')"><QuestionCircleOutlined/></Tooltip>
                    </span>
                }
            >
                <Input/>
            </Form.Item>

            <Form.Item
                name="agreement"
                valuePropName="checked"
                rules={[
                    {validator: (_, value) => checked ? Promise.resolve() : Promise.reject('Accept terms of service')},
                ]}
                {...tailFormItemLayout}
            >
                &#8203;<Checkbox value={checked} checked={checked} onClick={() => toggleCheck()}>
                I agree to the <a href="https://www.youtube.com/watch?v=CdKVX45wYeQ" target="_blank" rel="noreferrer">terms of service</a>
            </Checkbox>
            </Form.Item>
        </Form>
    );
}


// Override default button text color to match jooni logo blue
const StyledButton = styled(Button)`
  color: rgb(114,126,239);
`;

class CreateUserModal extends React.Component {

    state = {
        visible: false,
        confirmLoading: false
    };

    showModal = () => {
        this.setState({
            visible: true,
        });
    };

    handleOk = () => {
        formRef.current!.submit();
    };

    handleCancel = () => {
        formRef.current!.resetFields();
        this.setState({
            visible: false,
            confirmLoading: false
        });
    };

    render() {
        const { visible, confirmLoading} = this.state;
        return (
            <StyledButton type="link" style={{paddingLeft:0}} onClickCapture={this.showModal}>
                create user
                <Modal
                    title="Create User"
                    visible={visible}
                    onOk={this.handleOk}
                    confirmLoading={confirmLoading}
                    onCancel={this.handleCancel}
                    footer={[
                        <Button key="back" onClick={this.handleCancel}>
                            Cancel
                        </Button>,
                        <Button key="submit" type="primary" loading={confirmLoading} onClick={this.handleOk}>
                            Submit
                        </Button>,
                    ]}
                >
                    <NewUserForm modal={this}/>
                </Modal>
            </StyledButton>
        );
    }
}

export default CreateUserModal;